import React, { useEffect, useRef, useState, useCallback } from "react";
import { toast } from "sonner";
import { Megaphone, Send, Trash2, SmilePlus } from "lucide-react";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { MentionInput, renderMentions, timeAgo, EMOJIS } from "./CommentsDialog";
import { useAuth } from "../lib/auth";
import { ReportButton } from "./ReportButton";

const Reactions = ({ msg, me, onReact }) => (
  <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
    {Object.entries(msg.reactions || {}).map(([emoji, users]) => {
      const mine = users.some((u) => u.toLowerCase() === me);
      return (
        <button key={emoji} onClick={() => onReact(msg.id, emoji)} title={users.join(", ")}
          data-testid={`campaign-reaction-${msg.id}-${emoji}`}
          className={`flex items-center gap-1 rounded-full px-2 py-0.5 text-xs border transition-colors ${mine ? "bg-[#ccff00]/15 border-[#ccff00]/50 text-[#ccff00]" : "bg-slate-800/60 border-slate-700 text-slate-300 hover:border-slate-500"}`}>
          <span>{emoji}</span><span className="font-bold">{users.length}</span>
        </button>
      );
    })}
    <Popover>
      <PopoverTrigger asChild>
        <button data-testid={`campaign-add-reaction-${msg.id}`} className="w-6 h-6 rounded-full bg-slate-800/60 border border-slate-700 flex items-center justify-center text-slate-400 hover:text-[#ccff00]">
          <SmilePlus className="w-3.5 h-3.5" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-2 bg-[#121b2d] border-[#1e2c4a]" align="start">
        <div className="flex gap-1">
          {EMOJIS.map((e) => (
            <button key={e} onClick={() => onReact(msg.id, e)} data-testid={`campaign-pick-reaction-${msg.id}-${e}`}
              className="w-8 h-8 rounded-lg hover:bg-slate-800 text-lg flex items-center justify-center">{e}</button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  </div>
);

const Message = ({ msg, me, onReact, onDelete }) => {
  const { user } = useAuth();
  const mine = msg.name.toLowerCase() === me;
  const canDelete = mine || user?.role === "admin";
  return (
    <div data-testid={`campaign-msg-${msg.id}`} className={`rounded-xl px-3 py-2.5 border max-w-[85%] ${mine ? "ml-auto bg-[#ccff00]/8 border-[#ccff00]/25" : "bg-slate-900/60 border-slate-700/60"}`}>
      <div className="flex items-center justify-between gap-3">
        <span className="text-sm font-bold text-[#ccff00]">{msg.name}</span>
        <div className="flex items-center gap-2">
          <span className="text-[11px] text-slate-500">{timeAgo(msg.created_at)}</span>
          {!mine && (
            <ReportButton testid={`campaign-report-${msg.id}`} payload={{ kind: "campaign", target_id: msg.id, author: msg.name, text: msg.text }} />
          )}
          {canDelete && (
            <button onClick={() => onDelete(msg.id)} data-testid={`campaign-delete-${msg.id}`} className="text-slate-500 hover:text-[#ff4d4d]">
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>
      <p className="text-sm text-slate-200 mt-0.5 break-words">{renderMentions(msg.text)}</p>
      <Reactions msg={msg} me={me} onReact={onReact} />
    </div>
  );
};

export const CampaignChat = ({ playerName, names = [] }) => {
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const listRef = useRef(null);
  const lastId = useRef(null);
  const me = (playerName || "").toLowerCase();

  const load = useCallback(() => api.campaignMessages().then(setMsgs).catch(() => {}), []);
  useEffect(() => {
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [load]);
  useEffect(() => {
    const last = msgs[msgs.length - 1]?.id;
    if (last && last !== lastId.current && listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
    lastId.current = last;
  }, [msgs]);

  const act = async (fn) => {
    try { await fn(); await load(); } catch (e) { toast.error(e?.response?.data?.detail || "Operazione non riuscita"); }
  };
  const send = async () => {
    const t = text.trim();
    if (!t || busy) return;
    setBusy(true);
    await act(async () => { await api.campaignPost(t); setText(""); });
    setBusy(false);
  };

  return (
    <section className="mt-6 rounded-2xl bg-[#121b2d] border border-[#1e2c4a] p-4" data-testid="campaign-chat">
      <h3 className="font-heading font-bold text-lg text-slate-100 flex items-center gap-2">
        <Megaphone className="w-5 h-5 text-[#ff9f1c]" /> Campagna elettorale
      </h3>
      <p className="text-xs text-slate-500 mb-3">Chat aperta a tutti gli iscritti. Fai propaganda, usa @nome per chiamare in causa qualcuno.</p>
      <div ref={listRef} className="h-80 overflow-y-auto space-y-2 pr-1" data-testid="campaign-messages">
        {msgs.length === 0 ? (
          <p className="text-slate-500 text-sm text-center py-10" data-testid="campaign-empty">Nessun messaggio. Apri tu la campagna!</p>
        ) : msgs.map((m) => (
          <Message key={m.id} msg={m} me={me}
            onReact={(id, e) => act(() => api.campaignReact(id, e))}
            onDelete={(id) => act(() => api.campaignDelete(id))} />
        ))}
      </div>
      <div className="flex gap-2 pt-3 mt-3 border-t border-[#1e2c4a]">
        <MentionInput value={text} onChange={setText} onSubmit={send} names={names}
          placeholder="Scrivi alla campagna... usa @ per taggare" testid="campaign-input" />
        <Button onClick={send} disabled={busy || !text.trim()} data-testid="campaign-send-btn"
          className="bg-[#ccff00] hover:bg-[#aeea00] text-[#090d16] font-bold rounded-xl px-4 shrink-0">
          <Send className="w-4 h-4" />
        </Button>
      </div>
    </section>
  );
};
